<template>
    <div class="p-float-label">
        <InputText type="password" 
                    id="strong-pass-value" 
                    :placeholder="label" 
                    class="p-inputtext-lg" 
                    :modelValue="modelValue"
                    @update:modelValue="onChanged"
                    :class="{'p-invalid': v$.val.$invalid && v$.val.$dirty}"
                    :disabled="disabled" />
        <label for="strong-pass-value">{{ label }}</label>
    </div>
    <div v-if="showStrengthMeter" class="strength-meter-wrapper">
        <div class="strength-meter-label">
            <span>{{ t("strongPassword.messages.passwordStrength") }}</span>
            <span :class="['strength-meter-value', strengthClass]">{{ strengthLabel }}</span>
        </div>
        <div class="strength-meter-track" aria-hidden="true">
            <span class="strength-meter-fill" :class="strengthClass" :style="{ width: strengthWidth }" />
        </div>
    </div>
    <u-error-container v-if="v$.val.$invalid && v$.val.$dirty">
        <u-error-item v-if="v$.val.required.$invalid">
            {{ t("strongPassword.messages.enterNewPassword") }}
        </u-error-item>
        <u-error-item v-if="v$.val.strongPasswordValidation.$invalid">
            {{strongPasswordMessage}}
            <ul>
                <li>{{t("strongPassword.messages.strongPassword2")}}</li>
                <li>{{t("strongPassword.messages.strongPassword3")}}</li>
                <li>{{t("strongPassword.messages.strongPassword4")}}</li>
                <li>{{t("strongPassword.messages.strongPassword5")}}</li>
            </ul>
        </u-error-item>
    </u-error-container>
    <u-error-container v-if="v$.val.$dirty">
    <small>{{passwordRulesMessage}}</small>
     </u-error-container>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, watch } from 'vue';
import UErrorContainer from "@/components/common/UErrorContainer.vue";
import UErrorItem from "@/components/common/UErrorItem.vue";
import { required } from '@vuelidate/validators';
import { strongPassword } from '../../validation/Validators';
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import zxcvbn, { ZXCVBNResult } from 'zxcvbn';
import { DEFAULT_MIN_PASSWORD_SCORE } from '../../config/passwordPolicy';

export default defineComponent({
    components: { UErrorContainer, UErrorItem },
    props: {
        modelValue: String,
        isValid: Boolean,
        label: String,
        disabled: Boolean,
        minPasswordLength: Number,
        minPasswordScore: {
            type: Number,
            default: DEFAULT_MIN_PASSWORD_SCORE
        }
    },
    emits: ['update:modelValue', 'update:isValid'],

    setup(props, { emit }) {
        const { t } = useI18n();
        const form = reactive({
            val: props.modelValue ?? "",
        })

        const normalizedMinPasswordScore = computed(() => {
            const score = props.minPasswordScore ?? DEFAULT_MIN_PASSWORD_SCORE;

            return Math.min(4, Math.max(0, Math.round(score)));
        });

        const strengthTranslationKey = (score: number) => {
            if (score <= 1) {
                return 'strongPassword.messages.strengthWeak';
            }

            if (score === 2) {
                return 'strongPassword.messages.strengthFair';
            }

            if (score === 3) {
                return 'strongPassword.messages.strengthGood';
            }

            return 'strongPassword.messages.strengthStrong';
        };
        
        const strongPasswordMessage = computed(() => {
            return t("strongPassword.messages.strongPassword1", {
                length: props.minPasswordLength,
            })
        });

        const passwordRulesMessage = computed(() => {
            return t("changePassword.validations.passwordRules", {
                strength: t(strengthTranslationKey(normalizedMinPasswordScore.value))
            });
        });

        const passwordStrength = computed<ZXCVBNResult | null>(() => {
            if (!form.val) {
                return null;
            }

            return zxcvbn(form.val);
        });

        const showStrengthMeter = computed(() => Boolean(form.val));

        const strengthClass = computed(() => {
            const score = passwordStrength.value?.score ?? 0;

            if (score <= 1) {
                return 'strength-meter-fill--weak';
            }

            if (score === 2) {
                return 'strength-meter-fill--fair';
            }

            if (score === 3) {
                return 'strength-meter-fill--good';
            }

            return 'strength-meter-fill--strong';
        });

        const strengthLabel = computed(() => {
            const score = passwordStrength.value?.score ?? 0;

            return t(strengthTranslationKey(score));
        });

        const strengthWidth = computed(() => {
            const score = passwordStrength.value?.score;

            if (score == null) {
                return '0%';
            }

            return `${(score + 1) * 20}%`;
        });

        const passwordStrengthValidation = () => {
            if (!form.val) {
                return false;
            }

            return (passwordStrength.value?.score ?? 0) >= normalizedMinPasswordScore.value;
        };
            
        const strongPasswordValidation = () => {
            return strongPassword(form.val, props.minPasswordLength) && passwordStrengthValidation();
        };
        const rules = {
            val: { required, strongPasswordValidation }
        };

        const v$ = useVuelidate(rules, form);
        const passwordIsValid = computed(() => Boolean(form.val) && strongPasswordValidation());

        const onChanged = (event: string | undefined) => {
            form.val = event ?? "";
            v$.value.$touch();
            emit('update:modelValue', form.val);
        }

        watch(() => props.modelValue, (value) => {
            form.val = value ?? "";
            if (!form.val) {
                v$.value.$reset();
            }
        });

        watch(passwordIsValid, (value) => {
            emit('update:isValid', value);
        }, { immediate: true });

        return {
            form,
            v$,
            onChanged,
            t,
            passwordRulesMessage,
            strongPasswordMessage,
            showStrengthMeter,
            strengthClass,
            strengthLabel,
            strengthWidth
        }
    },
})
</script>

<style lang="scss" scoped>
ul ul {
    list-style-type: initial;
}

.strength-meter-wrapper {
    margin-bottom: 0.5rem;
}

.strength-meter-label {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.35rem;
    font-size: 0.9rem;
}

.strength-meter-track {
    height: 0.5rem;
    border-radius: 999px;
    background: #e5e7eb;
    overflow: hidden;
}

.strength-meter-fill {
    display: block;
    height: 100%;
    border-radius: inherit;
    transition: width 0.2s ease, background-color 0.2s ease;
}

.strength-meter-value {
    font-weight: 600;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
}

.strength-meter-fill--weak {
    background: #dc2626;
    color: white;
}

.strength-meter-fill--fair {
    background: #d97706;
    color: white;
}

.strength-meter-fill--good {
    background: #2563eb;
    color: white;
}

.strength-meter-fill--strong {
    background: #15803d;
    color: white;
}
</style>